import { useMemo, useState } from 'react'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { ExternalLink, Pencil, Plus, RefreshCw, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { DataTable, type Column } from '@/components/shared/data-table'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { apiError } from '@/lib/api-client'
import { currentLocale } from '@/lib/i18n'
import { useMyPermissions } from '@/hooks/use-settings'
import {
  useOaAccounts, useZnsTemplates, useSyncZnsTemplates,
  TEMPLATE_STATUS_LABELS, TEMPLATE_QUALITY_LABELS, TEMPLATE_TAG_LABELS, templateStatusVariant,
  type ZnsTemplate,
} from '@/hooks/use-zns'
import { ZnsTemplateEditorDialog } from './zns-template-editor-dialog'

const ALL = '__all__'

function money(v?: string | null) {
  const n = Number(v)
  return Number.isFinite(n) && v ? `${n.toLocaleString(currentLocale())}đ` : '—'
}

/** Tab "Mẫu tin": xem mọi mẫu (mọi trạng thái), tạo mẫu mới, sửa mẫu bị từ chối và gửi duyệt lại. */
export function ZnsTemplatesTab() {
  // Đổi tên `t` dịch thành `tt` vì tham số dòng (ZnsTemplate) trong các `cell` bên dưới cũng tên `t`.
  const { t: tt } = useTranslation('marketing')
  const accountsQuery = useOaAccounts()
  const [accountId, setAccountId] = useState('')
  const [status, setStatus] = useState(ALL)
  const [editorOpen, setEditorOpen] = useState(false)
  const [resubmit, setResubmit] = useState<ZnsTemplate | null>(null)

  const perms = useMyPermissions()
  const canCreate = !!perms.data?.has('zns.create')
  const canUpdate = !!perms.data?.has('zns.update')

  const effectiveAccountId = accountId || accountsQuery.data?.[0]?.id || ''
  const templatesQuery = useZnsTemplates(effectiveAccountId || undefined, false, true)
  const sync = useSyncZnsTemplates()

  const rows = useMemo(
    () => (templatesQuery.data ?? []).filter((t) => status === ALL || t.status === status),
    [templatesQuery.data, status],
  )

  async function handleSync() {
    try {
      const r = await sync.mutateAsync(effectiveAccountId)
      toast.success(tt('znsTemplates.syncedToast', { count: r.total }))
    } catch (err) {
      toast.error(apiError(err))
    }
  }

  function openCreate() {
    setResubmit(null)
    setEditorOpen(true)
  }

  function openResubmit(t: ZnsTemplate) {
    setResubmit(t)
    setEditorOpen(true)
  }

  const columns: Column<ZnsTemplate>[] = [
    {
      key: 'name',
      header: tt('znsTemplates.columns.name'),
      cell: (t) => (
        <div className="min-w-0">
          <p className="truncate font-medium">{t.templateName}</p>
          <p className="truncate text-xs tabular-nums text-muted-foreground">
            #{t.templateId}
            {t.tag ? ` · ${TEMPLATE_TAG_LABELS[t.tag] ?? t.tag}` : ''}
          </p>
        </div>
      ),
    },
    {
      key: 'status',
      header: tt('znsTemplates.columns.status'),
      cell: (t) => (
        <div className="space-y-1">
          <Badge variant={templateStatusVariant(t.status)}>{TEMPLATE_STATUS_LABELS[t.status] ?? t.status}</Badge>
          {t.rejectReason && (t.status === 'REJECT' || t.status === 'DISABLE') && (
            <p className="max-w-[260px] text-xs text-destructive">{t.rejectReason}</p>
          )}
        </div>
      ),
    },
    {
      key: 'quality',
      header: tt('znsTemplates.columns.quality'),
      cell: (t) => (t.quality ? TEMPLATE_QUALITY_LABELS[t.quality] ?? t.quality : '—'),
    },
    {
      key: 'price',
      header: tt('znsTemplates.columns.price'),
      cell: (t) => (
        <div className="text-xs tabular-nums">
          <p>{tt('znsTemplates.priceByPhone')} {money(t.priceSdt)}</p>
          <p className="text-muted-foreground">{tt('znsTemplates.priceByUid')} {money(t.priceUid)}</p>
        </div>
      ),
    },
    {
      key: 'updated',
      header: tt('znsTemplates.columns.updated'),
      cell: (t) => (
        <span className="text-xs text-muted-foreground tabular-nums">
          {dayjs(t.statusUpdatedAt ?? t.fetchedAt).format('DD/MM/YYYY HH:mm')}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      cell: (t) => (
        <div className="flex justify-end gap-1">
          {t.previewUrl && (
            <Button asChild size="sm" variant="ghost">
              <a href={t.previewUrl} target="_blank" rel="noreferrer">
                <ExternalLink className="h-4 w-4" /> {tt('znsTemplates.preview')}
              </a>
            </Button>
          )}
          {canUpdate && t.status === 'REJECT' && t.layout && (
            <Button size="sm" variant="outline" onClick={() => openResubmit(t)}>
              <Pencil className="h-4 w-4" /> {tt('znsTemplates.editResubmit')}
            </Button>
          )}
        </div>
      ),
    },
  ]

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <Select value={effectiveAccountId} onValueChange={setAccountId}>
          <SelectTrigger className="w-[260px]">
            <SelectValue placeholder={tt('znsTemplates.accountPlaceholder')} />
          </SelectTrigger>
          <SelectContent>
            {accountsQuery.data?.map((a) => (
              <SelectItem key={a.id} value={a.id}>
                {a.displayName || a.externalPageId || a.id}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-[180px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>{tt('znsTemplates.allStatuses')}</SelectItem>
            {Object.entries(TEMPLATE_STATUS_LABELS).filter(([v]) => v !== 'DELETE').map(([v, label]) => (
              <SelectItem key={v} value={v}>{label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="ml-auto flex gap-2">
          <Button variant="outline" onClick={handleSync} disabled={!effectiveAccountId || sync.isPending}>
            {sync.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            {tt('znsTemplates.syncButton')}
          </Button>
          {canCreate && (
            <Button onClick={openCreate} disabled={!effectiveAccountId}>
              <Plus className="h-4 w-4" /> {tt('znsTemplates.createButton')}
            </Button>
          )}
        </div>
      </div>
      <p className="text-sm text-muted-foreground">
        {tt('znsTemplates.hint')}
      </p>

      <DataTable
        columns={columns}
        rows={rows}
        loading={templatesQuery.isLoading || accountsQuery.isLoading}
        rowKey={(t) => t.id}
        emptyTitle={!effectiveAccountId ? tt('znsTemplates.noAccount') : tt('znsTemplates.empty')}
      />

      {effectiveAccountId && (
        <ZnsTemplateEditorDialog
          open={editorOpen}
          onOpenChange={setEditorOpen}
          accountId={effectiveAccountId}
          resubmit={resubmit}
        />
      )}
    </div>
  )
}
